import {
  ComponentProps,
  FC,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  RefObject,
} from 'react'

import { twMerge } from '~/lib/utils'

type ModalProps = {
  // NOTE: animationはpropsで渡すのではなくコンポーネント内で定義するようにしたい。
  animation: 'appear' | 'disappear' | undefined
  isOpen: boolean
  closeModal: () => void
  children: ReactNode
  modalPositionClassName?: string
  modalWidthClassName?: string
  modalHeightClassName?: string
}

const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation()
}

const useModalDisplayEffect = (
  modalRef: RefObject<HTMLDivElement>,
  animation: ComponentProps<typeof Modal>['animation'],
  isOpen: ComponentProps<typeof Modal>['isOpen'],
) => {
  useEffect(() => {
    const modalElement = modalRef.current
    if (modalElement) {
      if (animation === 'appear') {
        modalElement.style.display = 'block'
      }

      const handleAnimationEnd = () => {
        if (animation === 'disappear') {
          modalElement.style.display = 'none'
        }
      }

      modalElement.addEventListener('animationend', handleAnimationEnd)
      return () => {
        modalElement.removeEventListener('animationend', handleAnimationEnd)
      }
    }
  }, [isOpen, animation, modalRef])
}

export const Modal: FC<ModalProps> = ({
  children,
  closeModal,
  isOpen,
  animation,
  modalPositionClassName,
  modalWidthClassName,
  modalHeightClassName,
}) => {
  // モーダルが開いているときに外の要素をスクロールできないように
  useEffect(() => {
    if (isOpen) {
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    } else {
      document.body.style.position = ''
      document.body.style.width = ''
    }

    return () => {
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [isOpen])

  const modalRef = useRef<HTMLDivElement>(null)
  useModalDisplayEffect(modalRef, animation, isOpen)

  return (
    <div className="relative">
      {/* overlay */}
      <div
        aria-hidden
        className={twMerge(
          // NOTE: z-indexはreact-selectが9999となっているので、それよりも大きくしている。react-selectのz-indexを小さくしたい。
          'fixed inset-0 z-[10000] bg-overlayModal',
          `data-[state=close]:data-[animation=""]:hidden`, // 初期状態で非表示にする処理
          'data-[animation=appear]:animate-appear data-[animation=disappear]:animate-disappear',
          modalPositionClassName,
        )}
        data-animation={animation || ''}
        data-state={isOpen ? 'open' : 'close'}
        onClick={closeModal}
        ref={modalRef}
      >
        {/* モーダル */}
        <div
          aria-hidden
          className={twMerge(
            // NOTE: z-indexはreact-selectが9999となっているので、それよりも大きくしている。react-selectのz-indexを小さくしたい。
            'fixed right-0 z-[10010] w-full border border-grayBorder bg-white',
            modalWidthClassName,
            modalHeightClassName,
          )}
          onClick={stopPropagation}
        >
          <div className="h-full overflow-auto">{children}</div>
        </div>
      </div>
    </div>
  )
}
